import { Link } from "react-router-dom";
import { HeartHandshake, ListChecks, LayoutGrid, Volume2, Check, Crown, Building2, Users, ClipboardList, Mail, KeyRound, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxjYXJlZ2l2ZXIlMjBzbWlsaW5nJTIwaGVscGluZ3xlbnwwfHx8fDE3ODczNDM0MzB8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { user } = useAuth();
  return (
    <div data-testid="landing-page" className="min-h-screen bg-background">
      <header className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-10 h-10 rounded-2xl bg-primary grid place-items-center">
            <HeartHandshake size={24} strokeWidth={2.5} className="text-white" />
          </span>
          <span className="text-xl font-extrabold tracking-tight">RoutineBuddy</span>
        </div>
        <div className="flex items-center gap-2">
          {user ? (
            <Link to="/app" data-testid="go-to-app-link" className="min-h-[48px] px-5 rounded-full bg-primary text-white font-bold flex items-center transition-transform duration-200 ease-out hover:-translate-y-0.5">
              Open app
            </Link>
          ) : (
            <Link to="/login" data-testid="login-link" className="min-h-[48px] px-5 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center transition-colors duration-200 hover:bg-secondary">
              Log in
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 pb-24">
        <section className="grid md:grid-cols-2 gap-10 items-center py-10 sm:py-16">
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight fade-up">
              Calm days start with clear routines.
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-muted-foreground leading-relaxed fade-up" style={{ animationDelay: "120ms" }}>
              A free, friendly tool for carers and support workers. Build picture schedules and tap-to-speak communication boards for the people you support, without expensive apps.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 fade-up" style={{ animationDelay: "240ms" }}>
              <Link to="/login" data-testid="get-started-button" className="min-h-[56px] px-7 rounded-full bg-primary text-white text-lg font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5">
                Start free <Check size={22} strokeWidth={3} />
              </Link>
              <a href="#pricing" data-testid="see-pricing-link" className="min-h-[56px] px-7 rounded-full border-2 border-foreground/25 bg-white text-lg font-bold flex items-center transition-colors duration-200 hover:bg-secondary">
                See pricing
              </a>
            </div>
          </div>
          <img src={HERO_IMG} alt="A carer smiling while helping someone" className="rounded-3xl border-2 border-foreground/10 w-full object-cover aspect-[4/3] fade-up" style={{ animationDelay: "180ms" }} />
        </section>

        <section className="grid sm:grid-cols-2 gap-6 py-10">
          <div className="bg-white rounded-3xl border-2 border-foreground/10 p-8">
            <span className="w-14 h-14 rounded-2xl bg-primary/10 grid place-items-center">
              <ListChecks size={32} strokeWidth={2.5} className="text-primary" />
            </span>
            <h2 className="mt-5 text-2xl font-extrabold">Visual Schedules</h2>
            <p className="mt-3 text-lg text-muted-foreground leading-relaxed">
              Build step-by-step picture routines — morning, bedtime, anything. The person you support taps each big step to mark it done.
            </p>
          </div>
          <div className="bg-white rounded-3xl border-2 border-foreground/10 p-8">
            <span className="w-14 h-14 rounded-2xl bg-[#9F7AEA]/10 grid place-items-center">
              <LayoutGrid size={32} strokeWidth={2.5} className="text-[#9F7AEA]" />
            </span>
            <h2 className="mt-5 text-2xl font-extrabold">Communication Board</h2>
            <p className="mt-3 text-lg text-muted-foreground leading-relaxed">
              A grid of simple symbol tiles for needs, feelings, food and activities. Tap a tile and the device speaks it aloud.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-3xl border-2 border-foreground/10 p-8 flex items-start gap-4">
          <Volume2 size={36} strokeWidth={2.5} className="text-primary shrink-0 mt-1" />
          <p className="text-lg text-muted-foreground leading-relaxed">
            Designed for shared tablets: large high-contrast tap targets, a calming palette, and minimal reading. Voices use the device's built-in speech — nothing extra to buy.
          </p>
        </section>

        <section id="pricing" className="py-14">
          <h2 className="text-2xl sm:text-3xl font-extrabold">Simple pricing</h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            <div data-testid="free-plan-card" className="bg-white rounded-3xl border-2 border-foreground/10 p-8">
              <h3 className="text-xl font-extrabold">Free</h3>
              <p className="mt-1 text-3xl font-extrabold">£0 <span className="text-base font-semibold text-muted-foreground">forever</span></p>
              <ul className="mt-5 space-y-3 text-lg">
                <li className="flex gap-2"><Check size={24} className="text-primary shrink-0" strokeWidth={3} /> Unlimited visual routines, timers & alarms</li>
                <li className="flex gap-2"><Check size={24} className="text-primary shrink-0" strokeWidth={3} /> Try every Pro feature free — 1 hour, then a 6-hour rest</li>
                <li className="flex gap-2"><Check size={24} className="text-primary shrink-0" strokeWidth={3} /> Tap-to-speak talk board</li>
                <li className="flex gap-2"><Check size={24} className="text-primary shrink-0" strokeWidth={3} /> Works on any tablet or phone</li>
              </ul>
              <Link to="/login" data-testid="free-plan-button" className="mt-7 min-h-[56px] rounded-full border-2 border-foreground/25 font-bold text-lg flex items-center justify-center transition-colors duration-200 hover:bg-secondary">
                Start free
              </Link>
            </div>
            <div data-testid="premium-plan-card" className="bg-white rounded-3xl border-2 border-primary p-8 relative">
              <span className="absolute -top-4 left-6 bg-[#ECC94B] rounded-full px-4 py-1 font-bold text-sm flex items-center gap-1"><Crown size={16} strokeWidth={2.5} /> Best value</span>
              <h3 className="text-xl font-extrabold">Premium</h3>
              <p className="mt-1 text-3xl font-extrabold">£5 <span className="text-base font-semibold text-muted-foreground">/ month</span></p>
              <p className="text-sm font-semibold text-muted-foreground">or £50/year — 2 months free</p>
              <ul className="mt-5 space-y-3 text-lg">
                <li className="flex gap-2"><Check size={24} className="text-primary shrink-0" strokeWidth={3} /> All Pro features with no time limits</li>
                <li className="flex gap-2"><Check size={24} className="text-primary shrink-0" strokeWidth={3} /> Custom photo tiles & steps</li>
                <li className="flex gap-2"><Check size={24} className="text-primary shrink-0" strokeWidth={3} /> Multiple profiles, care plans & Word exports</li>
                <li className="flex gap-2"><Check size={24} className="text-primary shrink-0" strokeWidth={3} /> Sync across all your devices</li>
              </ul>
              <Link to="/login" data-testid="premium-plan-button" className="mt-7 min-h-[56px] rounded-full bg-primary text-white font-bold text-lg flex items-center justify-center transition-transform duration-200 ease-out hover:-translate-y-0.5">
                Go Premium
              </Link>
            </div>
          </div>
        </section>

        <section data-testid="org-section" className="py-14">
          <div className="bg-white rounded-3xl border-2 border-foreground/10 p-8 sm:p-10">
            <span className="w-14 h-14 rounded-2xl bg-[#4299E1]/10 grid place-items-center">
              <Building2 size={32} strokeWidth={2.5} className="text-[#4299E1]" />
            </span>
            <h2 className="mt-5 text-2xl sm:text-3xl font-extrabold">For care organisations</h2>
            <p className="mt-3 text-lg text-muted-foreground leading-relaxed max-w-2xl">
              One flat price for your whole team. Built for residential homes, supported living and day services.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Users, title: "Staff accounts with roles", body: "Invite your team by link. Owners manage; staff record. Everyone gets their own login." },
                { icon: ClipboardList, title: "Unlimited residents", body: "Routines, boards, mood logs and progress for every resident — one flat fee, not per head." },
                { icon: FileText, title: "Care notes with audit trail", body: "Photo and voice notes with author and timestamp on every entry. Owners can correct records." },
                { icon: Mail, title: "Automatic handovers", body: "Daily digests for your Care Circle and a Monday-morning summary email for the whole service." },
                { icon: KeyRound, title: "Integration API", body: "Read-only API keys let your care-management software pull routines, moods and notes." },
                { icon: Check, title: "GDPR ready", body: "UK data handling, self-serve deletion, no advertising trackers, plain-English privacy policy." },
              ].map((f) => (
                <div key={f.title} className="flex gap-3">
                  <f.icon size={28} strokeWidth={2.5} className="text-primary shrink-0 mt-1" />
                  <div>
                    <p className="font-extrabold text-lg">{f.title}</p>
                    <p className="text-muted-foreground font-semibold leading-relaxed">{f.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/login" data-testid="org-trial-button" className="min-h-[56px] px-7 rounded-full bg-[#4299E1] text-white text-lg font-bold flex items-center transition-transform duration-200 ease-out hover:-translate-y-0.5">
                Start 30-day free trial
              </Link>
              <p className="text-lg font-semibold text-muted-foreground">£15/month per organisation after. Cancel anytime.</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t-2 border-border py-8 text-center text-muted-foreground">
        <p>Made with care, for carers. RoutineBuddy is free to start — always.</p>
        <p className="mt-2 text-sm font-semibold">RoutineBuddy is a product of Simplerlivez LTD. Enquiries: <a href="mailto:hello@simplerlivez.com" className="text-primary underline">hello@simplerlivez.com</a></p>
        <Link to="/privacy" data-testid="privacy-link" className="inline-block mt-2 min-h-[48px] font-bold text-primary underline">Privacy & your data</Link>
        <Link to="/terms" data-testid="terms-link" className="inline-block mt-2 ml-4 min-h-[48px] font-bold text-primary underline">Terms of service</Link>
      </footer>
    </div>
  );
}
